import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AnonymizationConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const AnonymizationConfirmModal: React.FC<AnonymizationConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm
}) => {
  const { t } = useTranslation('inspire');

  if (!isOpen) return null;

  const items = [
    t('anonymization.confirmModal.itemName'),
    t('anonymization.confirmModal.itemEmail'),
    t('anonymization.confirmModal.itemAddress'),
    t('anonymization.confirmModal.itemPhoto'),
    t('anonymization.confirmModal.itemPhone'),
    t('anonymization.confirmModal.itemGender'),
    t('anonymization.confirmModal.itemAge'),
    t('anonymization.confirmModal.itemSignature'),
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[50000] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl sm:max-w-md w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle (mobile) */}
          <div className="flex justify-center pt-2 pb-0 sm:hidden">
            <div className="w-10 h-1 bg-slate-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 rounded-lg">
                <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-gray-900">
                {t('anonymization.confirmModal.title')}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3 sm:px-6 sm:py-4 space-y-3 sm:space-y-4">
            <p className="text-xs sm:text-sm text-gray-600 font-medium">
              {t('anonymization.confirmModal.subtitle')}
            </p>

            {/* Checklist */}
            <div className="bg-gray-50 rounded-lg p-2.5 sm:p-4 space-y-1.5 sm:space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex items-center gap-1.5 sm:gap-2">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-100 flex gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-3 py-2 sm:px-4 sm:py-2.5 border border-gray-300 text-gray-700 font-medium text-xs sm:text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('anonymization.confirmModal.buttonBack')}
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-medium text-xs sm:text-sm rounded-lg hover:from-pink-600 hover:to-pink-700 transition-all shadow-sm"
            >
              {t('anonymization.confirmModal.buttonConfirm')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AnonymizationConfirmModal;
